import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface AchievementRow {
  id:          string;
  title:       string;
  description: string;
  icon:        string;
  category:    string;
  points:      number;
  is_active:   boolean;
}

export function AchievementsPage() {
  const [all,   setAll]   = useState<AchievementRow[]>([]);
  const [mine,  setMine]  = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();

      const [{ data: list }, { data: unlocked }] = await Promise.all([
        supabase.from('achievements').select('*').eq('is_active', true).order('points'),
        user
          ? supabase.from('user_achievements').select('achievement_id').eq('user_id', user.id)
          : Promise.resolve({ data: [] }),
      ]);

      setAll(list ?? []);
      setMine(new Set((unlocked ?? []).map((r: { achievement_id: string }) => r.achievement_id)));
      setLoading(false);
    })();
  }, []);

  const unlockedCount  = mine.size;
  const totalPoints    = all.filter(a => mine.has(a.id)).reduce((s, a) => s + a.points, 0);
  const maxPoints      = all.reduce((s, a) => s + a.points, 0);

  const categories = ['exploration', 'consistency', 'depth', 'social', 'milestone'] as const;
  const catLabels: Record<string, string> = {
    exploration: 'Exploração',
    consistency: 'Consistência',
    depth:       'Profundidade',
    social:      'Social',
    milestone:   'Marcos',
  };

  return (
    <div className="min-h-screen bg-[#0D1130] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-[#C9A84C] text-2xl tracking-[8px] mb-2" aria-hidden="true">✦</div>
          <h1 className="text-[#C9A84C] text-3xl font-light mb-2">Conquistas</h1>
          <p className="text-[#F5ECD7]/70 mb-6">
            {loading ? '…' : `${unlockedCount} de ${all.length} desbloqueadas · ${totalPoints} de ${maxPoints} pontos`}
          </p>

          {/* Points bar */}
          <div className="max-w-md mx-auto h-1 bg-[#C9A84C]/20 rounded-full overflow-hidden" role="progressbar" aria-valuenow={totalPoints} aria-valuemax={maxPoints}>
            <div
              className="h-full bg-[#C9A84C] transition-all duration-700"
              style={{ width: maxPoints > 0 ? `${(totalPoints / maxPoints) * 100}%` : '0%' }}
            />
          </div>
        </div>

        {categories.map(cat => {
          const catItems = all.filter(a => a.category === cat);
          if (!catItems.length) return null;
          return (
            <div key={cat} className="mb-10">
              <h2 className="text-[#F5ECD7]/50 text-xs tracking-[3px] uppercase mb-4">{catLabels[cat]}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {catItems.map((a) => {
                  const got = mine.has(a.id);
                  return (
                    <div
                      key={a.id}
                      className={`p-5 text-center border rounded-sm transition-all ${
                        got
                          ? 'border-[#C9A84C] bg-[#C9A84C]/10'
                          : 'border-[#C9A84C]/20 opacity-40'
                      }`}
                      title={a.description}
                    >
                      <div className="text-4xl mb-3" aria-hidden="true">{got ? a.icon : '🔒'}</div>
                      <h3 className="text-[#C9A84C] text-xs font-light mb-1 leading-tight">{a.title}</h3>
                      <p className="text-[#F5ECD7]/50 text-[10px] leading-tight">{a.description}</p>
                      <p className="text-[#C9A84C]/60 text-[10px] mt-2 tracking-wider">{a.points} pts</p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="text-center py-12 text-[#F5ECD7]/40">Carregando conquistas…</div>
        )}
      </div>
    </div>
  );
}
